import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarPlus, Check, Search, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import type { FarmDetail, StaffDetail } from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export default function VisitPlanFormPage() {
  const navigate = useNavigate();
  const [farms, setFarms] = useState<FarmDetail[]>([]);
  const [staff, setStaff] = useState<StaffDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [selectedFarmerIds, setSelectedFarmerIds] = useState<string[]>([]);
  const [farmChoice, setFarmChoice] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    title: "",
    assignedStaffId: "",
    startDate: "",
    endDate: "",
    notes: "",
  });

  useEffect(() => {
    let active = true;
    Promise.all([
      services.farms.list({ pageSize: 100 }),
      services.staff.list({ pageSize: 100 }),
    ])
      .then(([farmResult, staffResult]) => {
        if (!active) return;
        setFarms(farmResult.items);
        setStaff(staffResult.items);
      })
      .catch((reason: Error) => {
        if (active) setLoadError(reason.message || "Planning reference data could not be loaded.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const farmers = useMemo(() => {
    const seen = new Map<string, { id: string; name: string; farms: FarmDetail[] }>();
    farms
      .filter((farm) => farm.farmStatus === "ACTIVE")
      .forEach((farm) => {
        const current = seen.get(farm.farmerId);
        if (current) current.farms.push(farm);
        else seen.set(farm.farmerId, { id: farm.farmerId, name: farm.farmerName, farms: [farm] });
      });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [farms]);

  const visibleFarmers = useMemo(
    () => farmers.filter((farmer) =>
      `${farmer.name} ${farmer.farms.map((farm) => farm.name).join(" ")}`
        .toLowerCase()
        .includes(search.toLowerCase()),
    ),
    [farmers, search],
  );

  const assignableStaff = useMemo(
    () => staff.filter((member) => {
      if (member.employmentStatus !== "ACTIVE") return false;
      const role = `${member.projectAssignment?.roleOnProject || ""} ${member.jobTitle || ""}`.toUpperCase();
      return role.includes("AGRONOMIST") || role.includes("FIELD_OFFICER") || role.includes("FIELD OFFICER");
    }),
    [staff],
  );

  const toggleFarmer = (farmerId: string, checked: boolean) => {
    setSelectedFarmerIds((current) =>
      checked ? Array.from(new Set([...current, farmerId])) : current.filter((id) => id !== farmerId),
    );
  };

  const selectVisible = () => {
    setSelectedFarmerIds((current) =>
      Array.from(new Set([...current, ...visibleFarmers.map((farmer) => farmer.id)])),
    );
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const multiFarmMissing = selectedFarmerIds.filter((farmerId) => {
      const farmer = farmers.find((item) => item.id === farmerId);
      return (farmer?.farms.length || 0) > 1 && !farmChoice[farmerId];
    });

    const nextErrors = [
      !form.title.trim() && "Visit Plan title is required.",
      !form.assignedStaffId && "Assign an agronomist or field officer.",
      !form.startDate && "Plan start date is required.",
      !form.endDate && "Plan end date is required.",
      !!form.startDate && !!form.endDate && form.endDate < form.startDate && "End date cannot be before start date.",
      selectedFarmerIds.length === 0 && "Select at least one farmer for this Visit Plan.",
      multiFarmMissing.length > 0 && "Choose the Farm for each farmer who has more than one active Farm.",
    ].filter(Boolean) as string[];

    setErrors(nextErrors);
    if (nextErrors.length) return;

    setSubmitting(true);
    try {
      const plan = await services.visits.createPlan({
        title: form.title.trim(),
        assignedStaffId: form.assignedStaffId,
        plannedStartDate: form.startDate,
        plannedEndDate: form.endDate,
        notes: form.notes.trim() || undefined,
        targets: selectedFarmerIds.map((farmerId, index) => {
          const farmer = farmers.find((item) => item.id === farmerId);
          return {
            farmerId,
            farmId: farmer?.farms.length === 1 ? farmer.farms[0].id : farmChoice[farmerId],
            sequenceNo: index + 1,
          };
        }),
      });
      toast.success("Visit Plan created", {
        description: `${selectedFarmerIds.length} farmer${selectedFarmerIds.length === 1 ? "" : "s"} added. The Plan is still a draft until sanctioned.`,
      });
      navigate(`/visits/plans/${plan.id}`);
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : "Visit Plan could not be created.";
      setErrors([message]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="visit-plan-form-page">
      <PageHeader
        eyebrow="Field Visits · Planning"
        title="Create Visit Plan"
        description="Assign many farmers to one field agenda. The coordinator sanctions the Plan before field staff can acknowledge and begin visits."
        backTo="/visits"
      />

      {loadError && (
        <Card className="border-destructive/30">
          <CardContent className="p-4 text-sm text-destructive">{loadError}</CardContent>
        </Card>
      )}

      {errors.length > 0 && (
        <Card className="border-destructive/30" data-testid="visit-plan-errors">
          <CardContent className="p-4">
            <p className="font-semibold text-destructive">Review the Visit Plan</p>
            <ul className="mt-2 list-disc pl-5 text-sm text-muted-foreground">
              {errors.map((error) => <li key={error}>{error}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}

      <form onSubmit={submit} className="space-y-5">
        <section className="form-section">
          <h2 className="text-lg font-semibold">Agenda & assignment</h2>
          <p className="mt-1 text-sm text-muted-foreground">This is the management schedule that will later be sanctioned and acknowledged.</p>
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="visit-plan-title">Plan title</Label>
              <Input
                id="visit-plan-title"
                value={form.title}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setForm((current) => ({ ...current, title: event.target.value }))}
                placeholder="e.g. Serere North field programme — Week 41"
                data-testid="visit-plan-title"
              />
            </div>
            <div className="space-y-2">
              <Label>Assigned agronomist / field officer</Label>
              <Select
                value={form.assignedStaffId}
                onValueChange={(value: string) => setForm((current) => ({ ...current, assignedStaffId: value }))}
                disabled={loading}
              >
                <SelectTrigger data-testid="visit-plan-staff"><SelectValue placeholder="Assign field staff" /></SelectTrigger>
                <SelectContent>
                  {assignableStaff.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name} · {member.projectAssignment?.roleOnProject || member.jobTitle}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="visit-plan-notes">Plan note</Label>
              <Input
                id="visit-plan-notes"
                value={form.notes}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setForm((current) => ({ ...current, notes: event.target.value }))}
                placeholder="Optional coordinator instruction"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="visit-plan-start">Start date</Label>
              <Input id="visit-plan-start" type="date" value={form.startDate} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setForm((current) => ({ ...current, startDate: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="visit-plan-end">End date</Label>
              <Input id="visit-plan-end" type="date" value={form.endDate} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setForm((current) => ({ ...current, endDate: event.target.value }))} />
            </div>
          </div>
        </section>

        <section className="form-section">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold"><UsersRound className="size-5 text-primary" />Farmers to visit</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Select the whole field programme in one operation. Farmers with one active Farm need no extra Farm selection.
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-semibold text-primary">{selectedFarmerIds.length}</p>
              <p className="text-xs text-muted-foreground">selected</p>
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)} className="pl-9" placeholder="Search farmer or Farm" />
            </div>
            <Button type="button" variant="outline" onClick={selectVisible} disabled={visibleFarmers.length === 0}>
              <Check className="mr-2 size-4" />Select visible
            </Button>
          </div>

          <div className="mt-4 max-h-[430px] space-y-2 overflow-y-auto pr-1">
            {visibleFarmers.map((farmer) => {
              const checked = selectedFarmerIds.includes(farmer.id);
              return (
                <div key={farmer.id} className={`rounded-xl border p-4 transition-colors ${checked ? "border-primary/30 bg-primary/[0.035]" : "bg-card"}`}>
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1 size-4"
                      checked={checked}
                      onChange={(event) => toggleFarmer(farmer.id, event.target.checked)}
                      data-testid={`visit-plan-farmer-${farmer.id}`}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="font-semibold">{farmer.name}</span>
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {farmer.farms.length === 1
                          ? `${farmer.farms[0].name} · canonical active Farm`
                          : `${farmer.farms.length} active Farms · choose the Farm below`}
                      </span>
                    </span>
                  </label>

                  {checked && farmer.farms.length > 1 && (
                    <div className="mt-3 pl-7">
                      <Label className="text-xs">Farm for this agenda</Label>
                      <Select
                        value={farmChoice[farmer.id] || ""}
                        onValueChange={(value: string) => setFarmChoice((current) => ({ ...current, [farmer.id]: value }))}
                      >
                        <SelectTrigger className="mt-1"><SelectValue placeholder="Choose Farm" /></SelectTrigger>
                        <SelectContent>
                          {farmer.farms.map((farm) => <SelectItem key={farm.id} value={farm.id}>{farm.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              );
            })}
            {!loading && visibleFarmers.length === 0 && (
              <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">No active farmer Farms match this search.</div>
            )}
          </div>
        </section>

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/visits")}>Cancel</Button>
          <Button type="submit" disabled={loading || submitting || !!loadError} data-testid="visit-plan-create-submit">
            <CalendarPlus className="mr-2 size-4" />
            {submitting ? "Creating Plan..." : `Create Draft Plan (${selectedFarmerIds.length})`}
          </Button>
        </div>
      </form>
    </div>
  );
}
